/**
 * Episode-of-Care–scoped chart visibility for providers.
 *
 * Medplum pilot AccessPolicy uses CareTeam compartments; the same patient may be in
 * multiple teams so both providers can open the chart. The medical record lists
 * clinical rows for every episode on the patient; **drill-in** (appointment page,
 * EoC detail, note modal, inline expand) is gated by care-manager rules below.
 */

import type { EpisodeOfCare, Practitioner } from '@medplum/fhirtypes';
import type { ClientChartBundle } from '../../pages/clients/data/clientChartBundle';
import type { Episode } from '../../pages/clients/data/types';
import type { Role } from '../../pages/users/types/rbac';
import { canAuthorClinicalNote } from '../chart/chartDocumentAccess';
import type { ResourceScope } from './scopeAccess';
import { getViewScope } from './scopeAccess';

export interface ChartScopeContext {
  viewScope: ResourceScope;
  /** Current user's Practitioner id when logged in as clinical staff. */
  practitionerId?: string;
  role: Role;
  /**
   * For supervisors: practitioner ids whose EpisodeOfCare care-managed charts this user may view.
   * Undefined means the supervisee set is unresolved; helpers then include only the viewer.
   */
  supervisedPractitionerIds?: readonly string[];
}

/** Minimal fields for EoC authorization (chart `Episode` or FHIR `EpisodeOfCare`). */
export interface EpisodeAuthorizationSlice {
  id: string;
  careManagerPractitionerId?: string;
}

function careManagerPractitionerIdFromFhir(eoc: EpisodeOfCare): string | undefined {
  const ref = eoc.careManager?.reference;
  if (!ref?.startsWith('Practitioner/')) return undefined;
  return ref.slice('Practitioner/'.length);
}

/** Practitioner ids to query via EpisodeOfCare?care-manager= for a supervisor caseload. */
export function supervisorCaseloadCareManagerSearchIds(ctx: ChartScopeContext): string[] {
  const ids = [...(ctx.supervisedPractitionerIds ?? [])];
  if (ctx.practitionerId && !ids.includes(ctx.practitionerId)) {
    ids.push(ctx.practitionerId);
  }
  return ids;
}

function isSupervisorTeamEpisode(
  careManagerPractitionerId: string | undefined,
  ctx: ChartScopeContext,
): boolean {
  if (ctx.supervisedPractitionerIds == null) {
    return false;
  }
  if (careManagerPractitionerId == null) {
    return true;
  }
  if (ctx.practitionerId != null && careManagerPractitionerId === ctx.practitionerId) {
    return true;
  }
  return ctx.supervisedPractitionerIds.includes(careManagerPractitionerId);
}

/**
 * Caseload: only `EpisodeOfCare` rows where the viewer is a participant via `careManager`
 * (aligned with chart authorization — not “every EoC on patients in my compartment”).
 */
export function filterEpisodeOfCareForCaseload(
  eocs: EpisodeOfCare[],
  ctx: ChartScopeContext,
): EpisodeOfCare[] {
  const slices: EpisodeAuthorizationSlice[] = eocs.map((eoc) => ({
    id: eoc.id ?? '',
    careManagerPractitionerId: careManagerPractitionerIdFromFhir(eoc),
  }));
  const allowed = new Set(getAuthorizedEpisodeIdsForChart(slices, ctx));
  return eocs.filter((eoc) => eoc.id != null && allowed.has(eoc.id));
}

/**
 * Episode ids whose **clinical rows** the user may see (not necessarily the full EoC panel list).
 */
export function getAuthorizedEpisodeIdsForChart(
  episodes: EpisodeAuthorizationSlice[],
  ctx: ChartScopeContext,
): string[] {
  const scope = ctx.viewScope;
  const ids = episodes.map((e) => e.id);

  if (scope === 'ALL' || scope === 'PRACTICE') {
    return ids;
  }
  if (scope === 'CUSTOM') {
    return ids;
  }
  if (scope === 'TEAM') {
    if (ctx.role === 'supervisor') {
      return episodes
        .filter((e) => isSupervisorTeamEpisode(e.careManagerPractitionerId, ctx))
        .map((e) => e.id);
    }
    return ids;
  }
  // OWN — include unassigned EoCs (intake creates EoCs without careManager;
  // Medplum AccessPolicy already gates API-level access).
  const pid = ctx.practitionerId;
  if (!pid) {
    return [];
  }
  return episodes
    .filter((e) => !e.careManagerPractitionerId || e.careManagerPractitionerId === pid)
    .map((e) => e.id);
}

/**
 * Whether the viewer may drill into an episode (detail page, appointment, notes) by care-manager rule.
 */
export function canViewEpisodeOfCareForContext(
  careManagerPractitionerId: string | undefined,
  ctx: ChartScopeContext,
): boolean {
  const cm = careManagerPractitionerId;
  const scope = ctx.viewScope;

  if (scope === 'ALL' || scope === 'PRACTICE') {
    return true;
  }
  if (scope === 'TEAM' && ctx.role === 'supervisor') {
    return isSupervisorTeamEpisode(cm, ctx);
  }
  if (scope === 'TEAM') {
    return true;
  }
  if (scope === 'CUSTOM') {
    return true;
  }
  return ctx.practitionerId != null && (cm == null || cm === ctx.practitionerId);
}

/**
 * Whether the viewer may author clinical documentation (treatment plans, clinical
 * notes) on an episode.
 *
 * Viewing and authoring are deliberately different rules. Practice owner and practice admin roles
 * read clinical documentation across the practice, but a `PRACTICE` / `ALL` view
 * scope never confers authorship: clinical documentation may only be created by a
 * clinical role on an episode that role is directly a member of.
 */
export function canAuthorClinicalDocumentationForEpisode(
  careManagerPractitionerId: string | undefined,
  ctx: ChartScopeContext,
): boolean {
  if (!canAuthorClinicalNote(ctx.role)) {
    return false;
  }
  if (ctx.role === 'supervisor') {
    return isSupervisorTeamEpisode(careManagerPractitionerId, ctx);
  }
  if (ctx.practitionerId == null) {
    return false;
  }
  // Intake creates episodes before a care manager is assigned; the first clinician
  // to document on one is how it gets picked up.
  return careManagerPractitionerId == null || careManagerPractitionerId === ctx.practitionerId;
}

/** Resolve the strongest held clinical role; active staff UI context must not remove clinical access. */
export function clinicalDocumentationAuthorRole(
  availableRoles: readonly Role[],
  activeRole: Role,
): Role {
  if (availableRoles.includes('supervisor')) return 'supervisor';
  if (availableRoles.includes('provider')) return 'provider';
  return activeRole;
}

/** Existing documents keep their episode identity even when it is outside the viewer's author scope. */
export function resolveChartDocumentEpisodeId(
  savedEpisodeId: string | undefined,
  initialEpisodeId: string | undefined,
  selectedEpisodeId: string | undefined,
  authorizedEpisodeIds: ReadonlySet<string>,
  firstAuthorizedEpisodeId: string | undefined,
): string | undefined {
  if (savedEpisodeId) return savedEpisodeId;
  if (initialEpisodeId && authorizedEpisodeIds.has(initialEpisodeId)) return initialEpisodeId;
  if (selectedEpisodeId && authorizedEpisodeIds.has(selectedEpisodeId)) return selectedEpisodeId;
  return firstAuthorizedEpisodeId;
}

/**
 * Whether the user may open the Episode of Care **detail page** (participant / care-manager rule).
 */
export function canViewEpisodeOfCarePage(episode: Episode, ctx: ChartScopeContext): boolean {
  return canViewEpisodeOfCareForContext(episode.careManagerPractitionerId, ctx);
}

/** Same rule as {@link canViewEpisodeOfCarePage} for a FHIR EpisodeOfCare (e.g. appointment RBAC). */
export function canViewMedplumEpisodeOfCare(eoc: EpisodeOfCare, ctx: ChartScopeContext): boolean {
  return canViewEpisodeOfCareForContext(careManagerPractitionerIdFromFhir(eoc), ctx);
}

/**
 * True if a clinical chart row should show for the current scope.
 * Unattributed rows: hidden for OWN when the patient has more than one episode (avoid leaking).
 */
/** How a provider may open an appointment from the client chart medical record. */
export type AppointmentChartAccess = 'full' | 'appointment-only';

/**
 * Drill-in for appointments: full session page on authorized episodes; appointment
 * metadata only (no clinical documentation) on another episode.
 */
export function getAppointmentChartAccess(
  episodeId: string | undefined,
  _hasLinkedClinicalNote: boolean,
  authorized: ReadonlySet<string>,
  patientEpisodeCount: number,
  viewScope: ResourceScope,
): AppointmentChartAccess {
  if (
    isEpisodeScopedRowVisible(episodeId, authorized, patientEpisodeCount, viewScope)
  ) {
    return 'full';
  }
  return 'appointment-only';
}

/** How a provider may open an assessment from the client chart measures / medical record. */
export type AssessmentChartAccess = 'full' | 'assessment-only';

/**
 * Assessments are always fully viewable across episodes.
 * Episode-scoped drill-in restrictions apply to appointments / progress notes only.
 * (`assessment-only` retained in the union for backward-compatible callers.)
 */
export function getAssessmentChartAccess(
  episodeId: string | undefined,
  authorized: ReadonlySet<string>,
  patientEpisodeCount: number,
  viewScope: ResourceScope,
): AssessmentChartAccess {
  void episodeId;
  void authorized;
  void patientEpisodeCount;
  void viewScope;
  return 'full';
}

export function isEpisodeScopedRowVisible(
  episodeId: string | undefined,
  authorized: ReadonlySet<string>,
  patientEpisodeCount: number,
  viewScope: ResourceScope,
  patientLevel = false,
): boolean {
  if (patientLevel) {
    return true;
  }
  const strictOrphans = viewScope === 'OWN' && patientEpisodeCount > 1;
  if (episodeId) {
    return authorized.has(episodeId);
  }
  if (strictOrphans) {
    return false;
  }
  return true;
}

/**
 * Filter clinical lists to authorized episodes only (legacy/tests). The client chart
 * prefers showing all rows and gating drill-in via {@link isEpisodeScopedRowVisible}.
 */
export function filterClientChartBundleByAuthorizedEpisodes(
  bundle: ClientChartBundle,
  authorizedIds: string[],
  viewScope: ResourceScope,
): ClientChartBundle {
  const auth = new Set(authorizedIds);
  const patientEpisodeCount = bundle.episodes.length;
  const allow = (eid: string | undefined, patientLevel = false) =>
    isEpisodeScopedRowVisible(eid, auth, patientEpisodeCount, viewScope, patientLevel);

  const filterTrends = (trends: typeof bundle.assessmentTrends) => {
    const dates = new Set(
      bundle.assessments.filter((a) => allow(a.episodeId)).map((a) => a.date),
    );
    return trends.filter((row) => dates.has(row.date));
  };

  return {
    ...bundle,
    episodes: bundle.episodes,
    appointments: bundle.appointments.filter((a) => allow(a.episodeId)),
    clinicalNotes: bundle.clinicalNotes.filter((n) => allow(n.episodeId, n.patientLevel)),
    completedForms: bundle.completedForms.filter((f) => allow(f.episodeId)),
    assessments: bundle.assessments.filter((a) => allow(a.episodeId)),
    assessmentTrends: filterTrends(bundle.assessmentTrends),
    documents: bundle.documents.filter((d) => allow(d.episodeId)),
    historicalRecords: bundle.historicalRecords.filter((h) => allow(h.episodeId)),
    tasks: bundle.tasks.filter((t) => allow(t.episodeId)),
    dischargeSummaries: bundle.dischargeSummaries.filter((d) => allow(d.episodeId)),
    adminNotes: bundle.adminNotes.filter((a) => allow(a.episodeId)),
    outstandingForms: bundle.outstandingForms,
  };
}

/** Build scope context from role + optional practitioner for chart filtering. */
export function chartScopeFromRoleAndPractitioner(
  role: Role,
  practitionerId: string | undefined,
  supervisedPractitionerIds?: readonly string[],
): ChartScopeContext {
  return {
    role,
    practitionerId,
    viewScope: getViewScope(role),
    ...(supervisedPractitionerIds != null ? { supervisedPractitionerIds } : {}),
  };
}

/**
 * Practitioners the user may select as **Primary provider** on a new calendar
 * appointment — aligned with caseload / chart scope (not the full Org directory).
 *
 * - OWN: only the logged-in practitioner.
 * - TEAM + supervisor: self plus direct supervisees.
 * - TEAM + other roles: all candidates (fallback; supervisor is the main TEAM role).
 * - ALL / PRACTICE / CUSTOM: unrestricted list (caller passes already-scoped search results).
 */
export function filterPractitionersForScheduling(
  practitioners: Practitioner[],
  ctx: ChartScopeContext,
): Practitioner[] {
  const scope = ctx.viewScope;
  const pid = ctx.practitionerId;

  if (scope === 'ALL' || scope === 'PRACTICE' || scope === 'CUSTOM') {
    return practitioners;
  }
  if (scope === 'TEAM') {
    if (ctx.role === 'supervisor') {
      const team = new Set(ctx.supervisedPractitionerIds ?? []);
      if (pid) team.add(pid);
      return practitioners.filter((p) => p.id != null && team.has(p.id));
    }
    return practitioners;
  }
  // OWN
  if (!pid) {
    return [];
  }
  return practitioners.filter((p) => p.id === pid);
}
